// 動畫系統（GDO-20）：重排（套用篩選時卡片移到新位置）用瀏覽器原生的 View Transitions；
// 卡片 → 詳情的層級轉換用複製的封面以 transform 飛過去（GDO-35：View Transitions 要截整頁畫面，
// 詳情資料在轉場中途載入時又要重截，開啟詳情明顯卡頓）。
// 不支援的瀏覽器、或使用者開啟「減少動態」時直接更新畫面，功能不受影響。
import { flushSync } from 'react-dom';

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => { finished: Promise<void> };
};

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export function canTransition(): boolean {
  return typeof document !== 'undefined' && !!(document as ViewTransitionDocument).startViewTransition;
}

/**
 * 在 View Transition 中同步套用更新。`kind` 寫在 <html data-vt>，CSS 依此決定哪些元素參與轉場
 * （重排時才替卡片命名，避免不必要的截圖）。
 */
export function withViewTransition(
  update: () => void,
  kind: 'reorder',
  hooks: { before?: () => void; after?: () => void; cleanup?: () => void } = {},
): void {
  const doc = document as ViewTransitionDocument;
  if (!doc.startViewTransition || prefersReducedMotion()) {
    update();
    return;
  }
  const root = document.documentElement;
  root.dataset.vt = kind;
  hooks.before?.();
  try {
    const vt = doc.startViewTransition(() => {
      flushSync(update);
      hooks.after?.();
    });
    vt.finished.finally(() => {
      delete root.dataset.vt;
      hooks.cleanup?.();
    });
  } catch {
    // 轉場無法開始（例如同一個名稱出現兩次）：直接更新
    delete root.dataset.vt;
    hooks.cleanup?.();
    update();
  }
}

/** 卡片上的封面（清單卡片或海報），開啟／關閉詳情時與詳情的封面接續 */
export function cardCover(id: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-game="${CSS.escape(id)}"] :is(.poster-img, .cover)`);
}

/** 元素在畫面上看得到（已掛在頁面上、有大小、至少一部分在可視範圍內） */
export function onScreen(el: Element | null | undefined): el is HTMLElement {
  if (!el?.isConnected) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < innerHeight && r.left < innerWidth;
}

/** CSS 時間轉毫秒（建置時壓縮會把 380ms 寫成 .38s） */
export function toMs(value: string): number {
  const v = value.trim();
  const n = Number.parseFloat(v);
  return Number.isNaN(n) ? 0 : v.endsWith('ms') ? n : v.endsWith('s') ? n * 1000 : n;
}

/** 讀取動畫 token（--dur-layer、--ease-emph 等），讓 JS 動畫與 CSS 用同一組時長與曲線 */
export function motionTiming(
  dur: '--dur-layer' | '--dur-enter',
  ease: '--ease' | '--ease-emph',
): KeyframeAnimationOptions {
  const css = getComputedStyle(document.documentElement);
  return {
    duration: toMs(css.getPropertyValue(dur)) || 300,
    easing: css.getPropertyValue(ease).trim() || 'ease-out',
    fill: 'both',
  };
}

/**
 * 共享元素轉場：複製 `from`（封面），放在 `to` 的位置與大小，再以 transform 從 `from` 的位置縮放過去。
 * 只動 transform，整段在合成執行緒上跑，不重排版面也不截圖。動畫結束或取消時移除複製品。
 */
export function flyCover(from: HTMLElement, to: DOMRect, host: HTMLElement): Animation {
  const a = from.getBoundingClientRect();
  const ghost = from.cloneNode(true) as HTMLElement;
  ghost.setAttribute('aria-hidden', 'true');
  ghost.removeAttribute('loading');
  ghost.className = `${ghost.className} cover-ghost`;
  Object.assign(ghost.style, {
    left: `${to.left}px`,
    top: `${to.top}px`,
    width: `${to.width}px`,
    height: `${to.height}px`,
    visibility: 'visible',
  });
  host.append(ghost);
  const sx = a.width / to.width;
  const sy = a.height / to.height;
  const anim = ghost.animate(
    [{ transform: `translate(${a.left - to.left}px, ${a.top - to.top}px) scale(${sx}, ${sy})` }, { transform: 'none' }],
    motionTiming('--dur-layer', '--ease-emph'),
  );
  const remove = () => ghost.remove();
  anim.addEventListener('finish', remove);
  anim.addEventListener('cancel', remove);
  return anim;
}
